'use client'

import {
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Button,
  EntityAboutSection,
  Textarea,
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  EntityDetailSkeleton,
  useRetryFitAnalysis,
  authorizedFetch,
  API_ORIGIN,
  isMarketplaceApplyCaptureEnabled,
  navigateWithThreadPreservation,
  listingProviderDetailHint,
  resolveListingProviderFromOpportunity,
} from '@yeshuman/sdk'
import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ExternalLink, FileText } from 'lucide-react'
import { JobBoardSaveButton } from './job-board-save-button'
import { CandidateJobFitSummaryWidget } from './candidate-job-fit-summary-widget'
import { useMarketplaceOpportunity } from '../../hooks/use-marketplace-opportunities'
import { useSavedOpportunities } from '../../hooks/use-saved-opportunities'
import { useCandidateJobsBackTarget } from '../../hooks/use-navigate-to-candidate-job-detail'
import { toast } from 'sonner'
import { ListingProviderIndicator } from '../listing-provider-indicator'

export function CandidateMarketplaceJobDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: job, isLoading, error } = useMarketplaceOpportunity(id)
  const {
    savedIds,
    savedOpportunities,
    pendingOpportunityIds,
    stuckOpportunityIds,
    toggleSave,
    savingOpportunityId,
  } = useSavedOpportunities()
  const { retryCandidate, mutatingOpportunityId } = useRetryFitAnalysis()
  const [note, setNote] = useState('')
  const [interestSaved, setInterestSaved] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [alphaDialogOpen, setAlphaDialogOpen] = useState(false)
  const jobsBack = useCandidateJobsBackTarget()
  const captureApply = isMarketplaceApplyCaptureEnabled()

  const expired = job?.marketplace_status === 'expired'
  const redirectUrl = job?.redirect_url
  const saved = job ? savedIds.has(job.id) : false
  const listingProvider = job
    ? resolveListingProviderFromOpportunity(job) ?? {
        key: 'adzuna',
        label: job.attribution_label || 'Jobs by Adzuna',
        href: job.attribution_url || 'https://www.adzuna.com.au/',
      }
    : null

  async function expressInterest(): Promise<boolean> {
    if (!job) return false
    setSubmitting(true)
    try {
      const response = await authorizedFetch(
        `${API_ORIGIN}/api/opportunities/marketplace/${encodeURIComponent(job.id)}/interest`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note }),
        },
      )
      if (response.ok) {
        setInterestSaved(true)
        return true
      }
      const error = await response.json().catch(() => ({}))
      toast.error((error as { error?: string }).error || 'Could not save your interest')
      return false
    } finally {
      setSubmitting(false)
    }
  }

  async function handlePrimaryApply() {
    if (!job || expired) return
    if (captureApply) {
      const ok = await expressInterest()
      if (ok) setAlphaDialogOpen(true)
      return
    }
    if (!redirectUrl) return
    window.open(redirectUrl, '_blank', 'noopener,noreferrer')
  }

  if (isLoading) return <EntityDetailSkeleton />
  if (error || !job) {
    return (
      <PageContainer>
        <PageHeader
          leading={
            <PageHeaderLeading back={jobsBack} />
          }
        />
        <p className="text-sm text-muted-foreground">Listing not found.</p>
      </PageContainer>
    )
  }

  const savedRow = savedOpportunities.find((row) => row.id === job.id) ?? null
  const showFitWidget = Boolean(savedRow || savedIds.has(job.id))
  const fitStuck = stuckOpportunityIds.has(job.id)
  const fitPending =
    pendingOpportunityIds.has(job.id) ||
    (!fitStuck && savedIds.has(job.id) && !savedRow)

  return (
    <PageContainer className="max-w-3xl">
      <PageHeader
        leading={
          <PageHeaderLeading back={jobsBack} />
        }
        title={job.title}
        description={job.company_display_name || job.organisation_name}
      />

      {showFitWidget ? (
        <CandidateJobFitSummaryWidget
          className="mb-6"
          insight={savedRow?.insight ?? null}
          pending={fitPending}
          stuck={fitStuck}
          onRetry={() => retryCandidate(job.id)}
          retryPending={mutatingOpportunityId === job.id}
        />
      ) : null}

      <EntityAboutSection
        title="Job description"
        leadingIcon={FileText}
        subtitle="Full text for this role"
        body={job.description}
        stripLeadingTitles={[job.title]}
        emptyVariant="text"
        emptyMessage="No description provided."
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{job.location || 'Location not specified'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {listingProvider ? (
            <ListingProviderIndicator
              label={job.attribution_label || listingProvider.label}
              logoUrl={listingProvider.logoUrl}
              href={job.attribution_url || listingProvider.href}
              variant="block"
              hint={listingProviderDetailHint(listingProvider.key)}
            />
          ) : null}
          {!expired ? (
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="interest-note">
                Optional note before you apply
              </label>
              <Textarea
                id="interest-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why this role interests you (saved to your TalentCo profile activity)"
                rows={3}
              />
              {interestSaved ? (
                <p className="text-xs text-muted-foreground" role="status">
                  {captureApply
                    ? 'Interest saved. External apply via Adzuna is paused during alpha.'
                    : 'Interest saved. You can apply when ready.'}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-destructive">This listing may no longer be available.</p>
          )}
        </CardContent>
        <CardFooter className="flex flex-wrap gap-2">
          <JobBoardSaveButton
            saved={saved}
            disabled={submitting}
            onClick={() => void toggleSave(job.id, saved)}
          />
          {!expired ? (
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={submitting || savingOpportunityId === job.id}
                onClick={() => void expressInterest()}
              >
                {interestSaved ? 'Interest saved' : 'Express interest'}
              </Button>
              <Button
                type="button"
                onClick={() => void handlePrimaryApply()}
                disabled={submitting || (!captureApply && !redirectUrl)}
              >
                Apply via Adzuna
                {!captureApply ? <ExternalLink className="ml-2 h-4 w-4" /> : null}
              </Button>
            </>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                navigateWithThreadPreservation(navigate, '/candidate/jobs', searchParams)
              }
            >
              Browse jobs
            </Button>
          )}
        </CardFooter>
      </Card>

      <AlertDialog open={alphaDialogOpen} onOpenChange={setAlphaDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alpha testing</AlertDialogTitle>
            <AlertDialogDescription>
              We&apos;ve recorded your interest in this role. Applying via Adzuna is paused while
              TalentCo is in alpha testing. You can still apply normally to TalentCo-hosted jobs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}
